import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KPI } from "@/components/DashboardWidgets";
import { Plus, Trash2, CheckCircle2, Printer, Wallet, Users } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["teacher", "driver", "admin", "librarian", "receptionist", "staff"];
const inr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;

export default function Payroll() {
  const [rows, setRows] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({
    employee_name: "", role: "teacher", month: new Date().toISOString().slice(0, 7),
    basic: "", allowances: "", deductions: "", status: "pending",
  });
  const [advForm, setAdvForm] = useState({ employee_name: "", amount: "", reason: "" });
  const [slipOpen, setSlipOpen] = useState(false);
  const [slipRow, setSlipRow] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);

  const load = async () => {
    try {
      const [pr, adv] = await Promise.all([
        api.get(`/erp/payroll?month=${monthFilter}`),
        api.get(`/erp/payroll/advances`),
      ]);
      setRows(pr.data); setAdvances(adv.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); }, [monthFilter]); // eslint-disable-line

  const totals = useMemo(() => {
    const paid = rows.filter((r) => r.status === "paid");
    return {
      count: rows.length,
      total_net: rows.reduce((a, r) => a + (r.net || 0), 0),
      paid_net: paid.reduce((a, r) => a + (r.net || 0), 0),
      pending_count: rows.length - paid.length,
    };
  }, [rows]);

  const create = async () => {
    if (!form.employee_name.trim()) { toast.error("Employee name required"); return; }
    try {
      await api.post("/erp/payroll", {
        ...form,
        basic: Number(form.basic) || 0,
        allowances: Number(form.allowances) || 0,
        deductions: Number(form.deductions) || 0,
      });
      toast.success("Payroll entry added");
      setForm({ ...form, employee_name: "", basic: "", allowances: "", deductions: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markPaid = async (id) => {
    try {
      await api.put(`/erp/payroll/${id}/pay`);
      toast.success("Marked paid");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this payroll entry?")) return;
    await api.delete(`/erp/payroll/${id}`);
    load();
  };

  const openSlip = (r) => { setSlipRow(r); setSlipOpen(true); };
  const printSlip = () => {
    const r = slipRow; if (!r) return;
    const w = window.open("", "_blank"); if (!w) return;
    w.document.write(`<html><head><title>Payslip ${r.employee_name} ${r.month}</title>
      <style>body{font-family:system-ui;padding:24px;max-width:640px;margin:auto;color:#0f172a}
      h1{margin:0;font-size:20px} .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #e2e8f0}
      .box{border:2px solid #0f172a;padding:16px;margin-top:16px} .total{font-weight:800;font-size:16px}</style>
      </head><body>
      <div style="display:flex;justify-content:space-between"><div><h1>Payslip</h1><div>Month: ${r.month}</div></div>
        <div style="text-align:right"><div class="total">${r.status === "paid" ? "PAID" : "PENDING"}</div>${r.paid_on ? `<div>${r.paid_on}</div>` : ""}</div></div>
      <div class="box">
        <div class="row"><b>Employee</b><span>${r.employee_name}</span></div>
        <div class="row"><b>Role</b><span>${r.role}</span></div>
        <div class="row"><b>Basic</b><span>${inr(r.basic)}</span></div>
        <div class="row"><b>Allowances</b><span>${inr(r.allowances)}</span></div>
        <div class="row"><b>Deductions</b><span>-${inr(r.deductions)}</span></div>
        <div class="row"><b>Net Pay</b><span class="total">${inr(r.net)}</span></div>
      </div>
      <p style="margin-top:24px;text-align:right;color:#64748b">Authorized Signatory</p>
      <script>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <DashLayout variant="school" title="Payroll">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI icon={Users} label="Employees this month" value={totals.count} color="text-blue-700 bg-blue-50" testId="pr-count" />
        <KPI icon={Wallet} label="Total Net Payroll" value={inr(totals.total_net)} color="text-purple-700 bg-purple-50" testId="pr-total" />
        <KPI icon={CheckCircle2} label="Paid" value={inr(totals.paid_net)} color="text-emerald-700 bg-emerald-50" testId="pr-paid" />
        <KPI icon={Wallet} label="Pending" value={totals.pending_count} color="text-amber-700 bg-amber-50" testId="pr-pending" />
      </div>

      <Card className="p-4 mb-4 flex flex-wrap gap-2 items-end">
        <div className="mr-4">
          <Label className="text-xs">Filter Month</Label>
          <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-40" data-testid="pr-filter-month" />
        </div>
        <div className="ml-auto flex flex-wrap gap-2 items-end">
          <Input placeholder="Employee name" value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} data-testid="pr-name" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="w-32" data-testid="pr-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="w-36" data-testid="pr-month" />
          <Input type="number" placeholder="Basic ₹ (locked after 1st save)" value={form.basic} onChange={(e) => setForm({ ...form, basic: e.target.value })} className="w-28" data-testid="pr-basic" />
          <Input type="number" placeholder="Allowances" value={form.allowances} onChange={(e) => setForm({ ...form, allowances: e.target.value })} className="w-28" data-testid="pr-allow" />
          <Input type="number" placeholder="Deductions" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} className="w-28" data-testid="pr-ded" />
          <Button onClick={create} className="bg-blue-700 hover:bg-blue-800" data-testid="pr-add"><Plus className="w-4 h-4 mr-1" />Add</Button>
          <Button variant="outline" onClick={() => setAdvOpen(true)} data-testid="pr-adv-open"><Wallet className="w-4 h-4 mr-1" />Advance</Button>
        </div>
      </Card>

      {advances.length > 0 && (
        <Card className="p-3 mb-4 bg-amber-50/40 border-amber-200">
          <div className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-2">Recent Advances (deducted from next payslip)</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {advances.slice(0, 10).map(a => (
              <Badge key={a.id} variant="outline" className="border-amber-300 text-amber-800 bg-white">
                {a.employee_name}: ₹{a.amount.toLocaleString("en-IN")} · {a.date}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Role</TableHead><TableHead>Month</TableHead>
            <TableHead className="text-right">Basic</TableHead><TableHead className="text-right">Allow.</TableHead>
            <TableHead className="text-right">Deduct.</TableHead><TableHead className="text-right">Net</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`pr-row-${r.id}`}>
                <TableCell className="font-semibold">{r.employee_name}</TableCell>
                <TableCell><Badge variant="outline">{r.role}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{r.month}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.basic)}</TableCell>
                <TableCell className="text-right tabular-nums text-emerald-700">{inr(r.allowances)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-700">{inr(r.deductions)}</TableCell>
                <TableCell className="text-right tabular-nums font-bold">{inr(r.net)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={r.status === "paid" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
                    {r.status}{r.paid_on ? ` · ${r.paid_on}` : ""}
                  </Badge>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {r.status !== "paid" && <Button size="sm" onClick={() => markPaid(r.id)} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`pr-pay-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark Paid</Button>}
                  <Button size="sm" variant="outline" onClick={() => openSlip(r)} data-testid={`pr-slip-${r.id}`}><Printer className="w-3.5 h-3.5 mr-1" />Slip</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">No payroll entries for {monthFilter}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={advOpen} onOpenChange={setAdvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Salary Advance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Employee name</Label><Input value={advForm.employee_name} onChange={(e) => setAdvForm({ ...advForm, employee_name: e.target.value })} data-testid="pr-adv-name" /></div>
            <div><Label className="text-xs">Amount ₹</Label><Input type="number" value={advForm.amount} onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} data-testid="pr-adv-amount" /></div>
            <div><Label className="text-xs">Reason</Label><Input value={advForm.reason} onChange={(e) => setAdvForm({ ...advForm, reason: e.target.value })} data-testid="pr-adv-reason" /></div>
            <Button onClick={async () => {
              try {
                await api.post("/erp/payroll/advances", { ...advForm, amount: Number(advForm.amount) || 0 });
                toast.success("Advance recorded");
                setAdvForm({ employee_name: "", amount: "", reason: "" });
                setAdvOpen(false); load();
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} className="bg-amber-600 hover:bg-amber-700 w-full" data-testid="pr-adv-submit">Save Advance</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={slipOpen} onOpenChange={setSlipOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Payslip Preview</DialogTitle></DialogHeader>
          {slipRow && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b pb-1"><b>Employee</b><span>{slipRow.employee_name}</span></div>
              <div className="flex justify-between border-b pb-1"><b>Role</b><span>{slipRow.role}</span></div>
              <div className="flex justify-between border-b pb-1"><b>Month</b><span>{slipRow.month}</span></div>
              <div className="flex justify-between border-b pb-1"><b>Basic</b><span>{inr(slipRow.basic)}</span></div>
              <div className="flex justify-between border-b pb-1"><b>Allowances</b><span className="text-emerald-700">+{inr(slipRow.allowances)}</span></div>
              <div className="flex justify-between border-b pb-1"><b>Deductions</b><span className="text-red-700">−{inr(slipRow.deductions)}</span></div>
              <div className="flex justify-between text-lg font-bold pt-2"><b>Net Pay</b><span>{inr(slipRow.net)}</span></div>
              <Button onClick={printSlip} className="w-full mt-3" data-testid="pr-slip-print"><Printer className="w-4 h-4 mr-1" />Print / Save PDF</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
